import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { loadStoreOptions } from '../util/loadStoreOptions';
import AddOrderModal from '../modals/AddOrderModal';
import { Order } from '../types';
import OrdersTable from '../components/OrdersTable';
import { useAuth } from '../hooks/useAuth';

const Orders = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showProfitLoss, setShowProfitLoss] = useState(searchParams.get('show_profit_loss') === 'true');
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [initialOrderData, setInitialOrderData] = useState<any>(null);
  const [duplicateSourceOrder, setDuplicateSourceOrder] = useState<Order | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [costsConfirmedFilter, setCostsConfirmedFilter] = useState(searchParams.get('costs_confirmed') || '');
  const [includeTest, setIncludeTest] = useState(searchParams.get('include_test') === 'true');
  const [invoiceNotExportedFilter, setInvoiceNotExportedFilter] = useState(searchParams.get('invoice_not_exported') || '');
  const [dateRangeFilter, setDateRangeFilter] = useState(searchParams.get('date_range') || '');
  const [autoExtractionFilter, setAutoExtractionFilter] = useState(searchParams.get('auto_extraction') || '');
  const [storeFilter, setStoreFilter] = useState(searchParams.get('store_id') || '');
  const [negativeProfitFilter, setNegativeProfitFilter] = useState(searchParams.get('negative_profit') || '');
  const [stuckFilter, setStuckFilter] = useState(searchParams.get('stuck') || '');
  const [referralDiscountFilter, setReferralDiscountFilter] = useState(searchParams.get('referral_discount') || '');
  const [invoiceRemindersPausedFilter, setInvoiceRemindersPausedFilter] = useState(searchParams.get('invoice_reminders_paused') || '');
  const [selectedStore, setSelectedStore] = useState<{ label: string; value: number } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const updateSearchParams = (overrides: Record<string, string> = {}) => {
    setSearchParams(prev => {
      const params: Record<string, string> = {};
      if (searchTerm) params['search'] = searchTerm;
      if (statusFilter) params['status'] = statusFilter;
      if (costsConfirmedFilter) params['costs_confirmed'] = costsConfirmedFilter;
      if (includeTest) params['include_test'] = 'true';
      if (invoiceNotExportedFilter) params['invoice_not_exported'] = invoiceNotExportedFilter;
      if (dateRangeFilter) params['date_range'] = dateRangeFilter;
      if (autoExtractionFilter) params['auto_extraction'] = autoExtractionFilter;
      if (storeFilter) params['store_id'] = storeFilter;
      if (negativeProfitFilter) params['negative_profit'] = negativeProfitFilter;
      if (stuckFilter) params['stuck'] = stuckFilter;
      if (referralDiscountFilter) params['referral_discount'] = referralDiscountFilter;
      if (invoiceRemindersPausedFilter) params['invoice_reminders_paused'] = invoiceRemindersPausedFilter;
      if (showProfitLoss) params['show_profit_loss'] = 'true';
      params['page'] = prev.get('page') || '1';
      return { ...params, ...overrides };
    });
  };

  const fetchOrders = async (search: string = '', page: number = 1, status: string = '', costsConfirmed: string = '', invoiceNotExported: string = '', dateRange: string = '', autoExtraction: string = '', storeId: string = '', negativeProfit: string = '', stuck: string = '', referralDiscount: string = '', invoiceRemindersPaused: string = '') => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('search', search);
      if (status) query.append('status', status);
      if (costsConfirmed) query.append('costs_confirmed', costsConfirmed);
      if (includeTest) query.append('include_test', 'true');
      if (invoiceNotExported) query.append('invoice_not_exported', invoiceNotExported);
      if (dateRange) query.append('date_range', dateRange);
      if (autoExtraction) query.append('auto_extraction', autoExtraction);
      if (storeId) query.append('store_id', storeId);
      if (stuck) query.append('stuck', stuck);
      if (referralDiscount) query.append('referral_discount', referralDiscount);
      if (invoiceRemindersPaused) query.append('invoice_reminders_paused', invoiceRemindersPaused);
      if (negativeProfit) query.append('negative_profit', negativeProfit);
      query.append('page', String(page));

      const res = await fetchWithAuth(`/orders?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwt')}`
        }
      });
      const data = await res.json();
      setOrders(data.orders ?? []);
      setTotalPages(data.total_pages);
      setCurrentPage(data.current_page);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const customerId = searchParams.get('customer_id');
    const isTiniMakingPurchase = searchParams.get('is_tini_making_purchase');
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const page = isNaN(initialPage) || initialPage < 1 ? 1 : initialPage;

    if (customerId) {
      const today = new Date().toISOString().split('T')[0];
      setInitialOrderData({
        customer_id: customerId,
        date: today,
        is_tini_making_purchase: isTiniMakingPurchase === 'true' ? 'yes' : 'no',
        origin: 'WEBSITE'
      });
      setShowAddModal(true);
    } else {
      fetchOrders(searchTerm, page, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateSearchParams({ page: '1' });
    fetchOrders(searchTerm, 1, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter, includeTest]);

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {user?.role === 'admin' && (
            <button
              onClick={() => {
                const next = !showProfitLoss;
                setShowProfitLoss(next);
                setSearchParams(prev => {
                  const params = new URLSearchParams(prev);
                  if (next) {
                    params.set('show_profit_loss', 'true');
                  } else {
                    params.delete('show_profit_loss');
                  }
                  return params;
                });
              }}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-300 flex-1 sm:flex-none"
            >
              {showProfitLoss ? 'Hide Profit/Loss' : 'Show Profit/Loss'}
            </button>
          )}
          <button onClick={() => setShowAddModal(true)} className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-32">
            Add Order
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-col md:flex-row flex-wrap items-stretch md:items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Search</label>
            <input
              type="text"
              placeholder="Search by Order Reference or Customer Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateSearchParams({ search: searchTerm, page: '1' });
                  fetchOrders(searchTerm, 1, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
                }
              }}
              className="border px-4 py-2 rounded-md w-full md:w-96"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All Orders</option>
              <option value="active">All Active (i.e no submitted/cancelled)</option>
              <option value="submitted">Submitted</option>
              <option value="paid">Paid</option>
              <option value="items_purchased">Items Purchased</option>
              <option value="ready_for_collection">Ready for Collection</option>
              <option value="collected">Collected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Costs</label>
            <select
              value={costsConfirmedFilter}
              onChange={(e) => setCostsConfirmedFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="false">Unconfirmed Costs</option>
              <option value="true">Confirmed Costs</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Invoice</label>
            <select
              value={invoiceNotExportedFilter}
              onChange={(e) => setInvoiceNotExportedFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="true">No Invoice Exported</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Date</label>
            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="today">Today</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Store</label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadStoreOptions}
              defaultOptions
              value={selectedStore}
              onChange={(option) => {
                setSelectedStore(option);
                setStoreFilter(option ? String(option.value) : '');
              }}
              placeholder="All"
              isClearable
              className="w-full md:w-48"
              styles={{
                control: (base) => ({ ...base, minHeight: '42px' }),
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Auto-extracted</label>
            <select
              value={autoExtractionFilter}
              onChange={(e) => setAutoExtractionFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="true">Auto-extracted</option>
              <option value="false">Not Auto-extracted</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Profit</label>
            <select
              value={negativeProfitFilter}
              onChange={(e) => setNegativeProfitFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="true">Loss-Making Only</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Stuck</label>
            <select
              value={stuckFilter}
              onChange={(e) => setStuckFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="true">Stuck 17+ Days</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="invoice-reminders-filter" className="text-xs font-medium text-gray-500">Invoice reminders</label>
            <select
              id="invoice-reminders-filter"
              value={invoiceRemindersPausedFilter}
              onChange={(e) => setInvoiceRemindersPausedFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="true">Paused</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="referral-discount-filter" className="text-xs font-medium text-gray-500">Referral discount</label>
            <select
              id="referral-discount-filter"
              value={referralDiscountFilter}
              onChange={(e) => setReferralDiscountFilter(e.target.value)}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="true">With a referral discount</option>
              <option value="false">Without a referral discount</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Test orders</label>
            <select
              value={includeTest ? 'true' : ''}
              onChange={(e) => setIncludeTest(e.target.value === 'true')}
              className="border px-4 py-2 rounded-md w-full md:w-auto"
            >
              <option value="">Hidden</option>
              <option value="true">Shown</option>
            </select>
          </div>
          <div className="flex items-end gap-2 md:ml-auto">
            {searchTerm && (
              <button
                onClick={() => {
                  updateSearchParams({ search: searchTerm, page: '1' });
                  fetchOrders(searchTerm, 1, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
                }}
                className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-32"
              >
                Search
              </button>
            )}
            {(searchTerm || statusFilter || costsConfirmedFilter || invoiceNotExportedFilter || dateRangeFilter || autoExtractionFilter || storeFilter || negativeProfitFilter || stuckFilter || referralDiscountFilter || invoiceRemindersPausedFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setCostsConfirmedFilter('');
                  setInvoiceNotExportedFilter('');
                  setDateRangeFilter('');
                  setAutoExtractionFilter('');
                  setStoreFilter('');
                  setNegativeProfitFilter('');
                  setStuckFilter('');
                  setReferralDiscountFilter('');
                  setInvoiceRemindersPausedFilter('');
                  setSelectedStore(null);
                  setSearchParams(showProfitLoss ? { page: '1', show_profit_loss: 'true' } : { page: '1' });
                  fetchOrders('', 1, '', '', '', '', '', '', '', '', '', '');
                }}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-400"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}
      <OrdersTable
        orders={orders}
        showProfitLoss={showProfitLoss}
        openActionMenuId={openActionMenuId}
        setOpenActionMenuId={setOpenActionMenuId}
        onEdit={(order) => {
          setDuplicateSourceOrder(null);
          setSelectedOrder(order);
          setShowAddModal(true);
        }}
        onDuplicate={(order) => {
          setSelectedOrder(null);
          setDuplicateSourceOrder(order);
          setShowAddModal(true);
        }}
        onSuccess={() => fetchOrders(searchTerm, currentPage, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter)}
      />
      <div className="flex justify-center items-center space-x-4 mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => {
            const newPage = currentPage - 1;
            updateSearchParams({ page: String(newPage) });
            fetchOrders(searchTerm, newPage, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
          }}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => {
            const newPage = currentPage + 1;
            updateSearchParams({ page: String(newPage) });
            fetchOrders(searchTerm, newPage, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
          }}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
      <AddOrderModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedOrder(null);
          setDuplicateSourceOrder(null);
          setInitialOrderData(null);
          window.history.replaceState({}, document.title, "/orders");
        }}
        onSuccess={() => {
          setShowAddModal(false);
          setSelectedOrder(null);
          setDuplicateSourceOrder(null);
          setInitialOrderData(null);
          window.history.replaceState({}, document.title, "/orders");
          fetchOrders(searchTerm, currentPage, statusFilter, costsConfirmedFilter, invoiceNotExportedFilter, dateRangeFilter, autoExtractionFilter, storeFilter, negativeProfitFilter, stuckFilter, referralDiscountFilter, invoiceRemindersPausedFilter);
        }}
        order={selectedOrder}
        duplicateFromOrder={duplicateSourceOrder}
        initialData={initialOrderData}
      />
    </div>
  );
};

export default Orders;
